import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import { Box, Button, IconButton, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ScoutIcon from "components/ScoutIcon";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";

import type { StreamStatus } from "../../../../stream";
import type { ScoutActiveTurn } from "../hooks/types";
import type { ScoutConversationPrivateDocumentRow } from "../hooks/useScoutConversationPrivateDocuments";
import type { ScoutAttachment } from "../scoutSubmitAttachments";

import { isScoutOptimisticConversationId } from "../hooks/scoutConversationIds";
import { isSyntheticInternalEventMessage } from "../hooks/scoutInternalEvents";
import { SCOUT_WRITE_MODE_GQL, type ScoutWriteModeGql } from "../hooks/scoutWriteMode";
import { canRetryLastUserTurn, getLastUserTurnForRetry } from "../scoutRetryPayload";
import { AgentMessage } from "./message/AgentMessage";
import {
  getTurnErrorMessage,
  isCurrentAgentTurnComplete,
  isNonRetriableTerminalErrorCode,
} from "./message/scoutAgentTurnUi";
import { type ScoutChatMessage } from "./message/scoutChatMessage";
import { useScoutMessageSpacing } from "./message/scoutMessageSpacing";
import { SCOUT_TURN_ANCHOR_TOP_GAP_PX, UserMessage } from "./message/UserMessage";
import { ScoutChatDropzone } from "./ScoutChatDropzone";
import { ScoutInput, type ScoutSubmitHandler } from "./ScoutInput";
import { ScoutLargeDocumentOptInButtons } from "./ScoutLargeDocumentOptInButtons";
import { deriveConversationTodoEpochs, getHostedTodoEpochsForMessage } from "./scoutTodoSnapshot";

const JUMP_TO_LATEST_THRESHOLD_PX = 24;
// Minimum bottom padding reserved for the overlaid footer before we have a
// measured footer height. Keep this near the normal composer height.
const SCOUT_DEFAULT_FOOTER_CLEARANCE_PX = 176;
// Extra breathing room between the measured footer and the last readable line
// of Scout content. Increase if content feels too close to the composer.
const SCOUT_FOOTER_CLEARANCE_GAP_PX = 24;
// Extra height for the bottom fade beyond the footer clearance. Increase if
// messages show sharply behind the composer; decrease if the fade feels tall.
const SCOUT_BOTTOM_FADE_EXTRA_PX = 64;
// Fallback jump-button offset for the normal composer before measurement.
// Keep this near the old visual placement for the no-attachment state.
const SCOUT_JUMP_TO_LATEST_DEFAULT_BOTTOM_PX = 116;
// Gap between the top of the measured footer and the jump-to-latest button.
// Increase if the button feels too close to uploaded files or the composer.
const SCOUT_JUMP_TO_LATEST_FOOTER_GAP_PX = 8;

function getMessageInstanceKey(message: ScoutChatMessage): string {
  return message.clientInstanceId ?? message.id;
}

function findLastUserMessage(messages: ScoutChatMessage[]): ScoutChatMessage | undefined {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message?.role === "user") {
      return message;
    }
  }
  return undefined;
}

function getTurnAnchorContentOffset(scrollContainer: HTMLElement, turnAnchor: HTMLElement): number {
  const containerRect = scrollContainer.getBoundingClientRect();
  const anchorRect = turnAnchor.getBoundingClientRect();
  return anchorRect.top - containerRect.top + scrollContainer.scrollTop;
}

function getActiveTurnContentBottomOffset(
  scrollContainer: HTMLElement,
  activeTurnContainer: HTMLElement
): number {
  const containerRect = scrollContainer.getBoundingClientRect();
  // The active-turn container reserves a spacer via minHeight, so its own
  // bottom edge sits below the empty canvas. Measure the last rendered child
  // instead — that's where the real reply content actually ends.
  const lastContentChild = activeTurnContainer.lastElementChild;
  const measuredElement =
    lastContentChild instanceof HTMLElement ? lastContentChild : activeTurnContainer;
  const contentRect = measuredElement.getBoundingClientRect();
  return contentRect.bottom - containerRect.top + scrollContainer.scrollTop;
}

// Errors thrown from the GraphQL `SendScoutMessage` mutation (e.g. the API
// couldn't accept the message at all) carry `activeTurn.couldNotStart=true`
// — we don't want to also render an inline retry bubble for them since the
// turn never started. The in-chat retry experience stays scoped to mid-turn
// failures where Scout actually started a turn and then couldn't finish.

const ScoutDisclaimer = memo(function ScoutDisclaimer() {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: "block", textAlign: "center", mt: 1.5, px: 2 }}
    >
      Scout is AI and can make mistakes. Please double-check responses.
    </Typography>
  );
});
ScoutDisclaimer.displayName = "ScoutDisclaimer";

const ChatFooter = memo(
  function ChatFooter({
    tenantId,
    conversationId,
    largeDocumentOptIns,
    showLargeDocumentOptInButtons,
    onDismissLargeDocumentOptIn,
    onConfirmLargeDocumentAnalysis,
    isConfirmingLargeDocumentAnalysis,
    isDecliningLargeDocumentAnalysis,
    onSubmit,
    onAbort,
    isSending,
    isAborting,
    autoFocus,
    onAttachmentsUploaded,
    onAttachmentRemoved,
    writeMode,
    onWriteModeChange,
    hasScoutProAccess,
    lastUserMessage,
    inputPlaceholder,
    footerRef,
    scrollbarGutterPx,
    contentPaddingX = 2,
  }: {
    tenantId?: string;
    conversationId?: string;
    largeDocumentOptIns: ScoutConversationPrivateDocumentRow[];
    showLargeDocumentOptInButtons: boolean;
    onDismissLargeDocumentOptIn?: (privateDocumentIds: string[]) => void | Promise<void>;
    onConfirmLargeDocumentAnalysis?: (input: {
      privateDocumentIds: string[];
      documents: ScoutConversationPrivateDocumentRow[];
    }) => void | Promise<void>;
    isConfirmingLargeDocumentAnalysis?: boolean;
    isDecliningLargeDocumentAnalysis?: boolean;
    onSubmit: ScoutSubmitHandler;
    onAbort: () => void;
    isSending: boolean;
    isAborting: boolean;
    autoFocus: boolean;
    onAttachmentsUploaded?: (attachments: ScoutAttachment[]) => void;
    onAttachmentRemoved?: (privateDocumentId: string) => void | Promise<void>;
    writeMode?: ScoutWriteModeGql;
    onWriteModeChange?: (next: ScoutWriteModeGql) => void;
    hasScoutProAccess?: boolean;
    lastUserMessage?: string;
    inputPlaceholder?: string;
    footerRef: RefObject<HTMLElement | null>;
    scrollbarGutterPx?: number;
    contentPaddingX?: number;
  }) {
    const theme = useTheme();

    return (
      <Box
        component="footer"
        ref={footerRef}
        sx={{
          // Overlay the bottom of the canvas so the message scroll surface (and
          // its scrollbar) spans the full chat height behind it.
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          pt: 1,
          pb: 2,
          px: contentPaddingX,
          pointerEvents: "none",
        }}
      >
        {/* Opaque backdrop that masks messages scrolling behind the composer.
            It stops short of the scrollbar gutter so the scrollbar stays
            visible to the bottom edge, while the composer content below keeps
            its full width (no cramped wrapping of the disclaimer). */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            right: `${scrollbarGutterPx ?? 0}px`,
            zIndex: -1,
            bgcolor: theme.palette.scoutCanvas,
          }}
        />
        <Box sx={{ pointerEvents: "auto" }}>
          {showLargeDocumentOptInButtons && largeDocumentOptIns.length > 0 ? (
            <ScoutLargeDocumentOptInButtons
              documents={largeDocumentOptIns}
              isConfirming={isConfirmingLargeDocumentAnalysis ?? false}
              isDeclining={isDecliningLargeDocumentAnalysis ?? false}
              onConfirm={(privateDocumentIds) => {
                void (onConfirmLargeDocumentAnalysis ?? (async () => {}))({
                  privateDocumentIds,
                  documents: largeDocumentOptIns,
                });
              }}
              onDecline={() => {
                void onDismissLargeDocumentOptIn?.(
                  largeDocumentOptIns.map((document) => document.id)
                );
              }}
            />
          ) : null}
          <ScoutInput
            tenantId={tenantId}
            conversationId={conversationId}
            onSubmit={onSubmit}
            onAbort={onAbort}
            isSending={isSending}
            isAborting={isAborting}
            onAttachmentsUploaded={onAttachmentsUploaded}
            onAttachmentRemoved={onAttachmentRemoved}
            placeholder={inputPlaceholder ?? "Ask Scout a follow-up question"}
            autoFocus={autoFocus}
            lastUserMessage={lastUserMessage}
            writeMode={writeMode}
            onWriteModeChange={onWriteModeChange}
            hasScoutProAccess={hasScoutProAccess}
          />
        </Box>
        <ScoutDisclaimer />
      </Box>
    );
  },
  (prev, next) =>
    prev.tenantId === next.tenantId &&
    prev.conversationId === next.conversationId &&
    prev.largeDocumentOptIns.length === next.largeDocumentOptIns.length &&
    prev.largeDocumentOptIns.every((document, index) => {
      const nextDocument = next.largeDocumentOptIns[index];
      return (
        document.id === nextDocument?.id &&
        document.extractionStatus === nextDocument.extractionStatus &&
        document.previewSummary === nextDocument.previewSummary
      );
    }) &&
    prev.showLargeDocumentOptInButtons === next.showLargeDocumentOptInButtons &&
    prev.isConfirmingLargeDocumentAnalysis === next.isConfirmingLargeDocumentAnalysis &&
    prev.isDecliningLargeDocumentAnalysis === next.isDecliningLargeDocumentAnalysis &&
    prev.onConfirmLargeDocumentAnalysis === next.onConfirmLargeDocumentAnalysis &&
    prev.onSubmit === next.onSubmit &&
    prev.onAbort === next.onAbort &&
    prev.isSending === next.isSending &&
    prev.isAborting === next.isAborting &&
    prev.autoFocus === next.autoFocus &&
    prev.onAttachmentsUploaded === next.onAttachmentsUploaded &&
    prev.onAttachmentRemoved === next.onAttachmentRemoved &&
    prev.writeMode === next.writeMode &&
    prev.onWriteModeChange === next.onWriteModeChange &&
    prev.hasScoutProAccess === next.hasScoutProAccess &&
    prev.lastUserMessage === next.lastUserMessage &&
    prev.inputPlaceholder === next.inputPlaceholder &&
    prev.footerRef === next.footerRef &&
    prev.scrollbarGutterPx === next.scrollbarGutterPx &&
    prev.contentPaddingX === next.contentPaddingX
);
ChatFooter.displayName = "ChatFooter";

export function ChatChrome({
  tenantId,
  conversationId,
  messages,
  activeTurn,
  streamStatus,
  loading = false,
  initialPinned = false,
  onSubmit,
  onAbort,
  isSending,
  isAborting,
  onLoadOlderMessages,
  isLoadingOlderMessages = false,
  hasOlderMessages = false,
  showFooter = true,
  inputAutoFocus = true,
  privateDocuments = [],
  largeDocumentOptIns,
  onDismissLargeDocumentOptIn = () => {},
  onConfirmLargeDocumentAnalysis = async () => {},
  isConfirmingLargeDocumentAnalysis = false,
  isDecliningLargeDocumentAnalysis = false,
  onAttachmentsUploaded,
  onAttachmentRemoved,
  writeMode = SCOUT_WRITE_MODE_GQL.OFF,
  onWriteModeChange,
  hasScoutProAccess = true,
  bottomFadeHeight,
  inputPlaceholder,
  messagePaddingTop,
  headerOverlayPx = 0,
  footerScrollbarGutterPx = 0,
  footerContentPaddingX = 2,
}: {
  tenantId?: string;
  conversationId?: string;
  messages: ScoutChatMessage[];
  activeTurn?: ScoutActiveTurn;
  streamStatus?: StreamStatus;
  loading?: boolean;
  initialPinned?: boolean;
  onSubmit: ScoutSubmitHandler;
  onAbort: () => void;
  isSending: boolean;
  isAborting: boolean;
  onLoadOlderMessages?: () => Promise<void>;
  isLoadingOlderMessages?: boolean;
  hasOlderMessages?: boolean;
  showFooter?: boolean;
  inputAutoFocus?: boolean;
  privateDocuments?: ScoutConversationPrivateDocumentRow[];
  largeDocumentOptIns?: ScoutConversationPrivateDocumentRow[];
  onDismissLargeDocumentOptIn?: (privateDocumentIds: string[]) => void | Promise<void>;
  onConfirmLargeDocumentAnalysis?: (input: {
    privateDocumentIds: string[];
    documents: ScoutConversationPrivateDocumentRow[];
  }) => void | Promise<void>;
  isConfirmingLargeDocumentAnalysis?: boolean;
  isDecliningLargeDocumentAnalysis?: boolean;
  onAttachmentsUploaded?: (attachments: ScoutAttachment[]) => void;
  onAttachmentRemoved?: (privateDocumentId: string) => void | Promise<void>;
  writeMode?: ScoutWriteModeGql;
  onWriteModeChange?: (next: ScoutWriteModeGql) => void;
  hasScoutProAccess?: boolean;
  /**
   * Height of the bottom fade gradient that masks the messages behind the
   * input footer. Defaults to the full-page-sized responsive value; pass a
   * smaller number for compact surfaces like the popover.
   */
  bottomFadeHeight?: number;
  /** Placeholder text for the input in the footer. Defaults to the follow-up copy. */
  inputPlaceholder?: string;
  /**
   * Override the top padding above the first message. Defaults to the full-
   * page value that clears the sticky AppBar; compact surfaces (the popover)
   * override with a smaller value so short conversations sit at the top of
   * the chat area instead of drifting toward the middle.
   */
  messagePaddingTop?: number;
  /**
   * Height of a floating header overlay that sits on top of the scroll
   * surface (full page only). The scroll surface spans the full height so its
   * scrollbar bleeds to the top edge; this inset keeps message content — and
   * the turn-anchor auto-scroll — clear of the header instead of hidden
   * behind it. Defaults to 0 for surfaces without an overlaid header.
   */
  headerOverlayPx?: number;
  /**
   * Right gutter (px) reserved for the scroll surface's scrollbar behind the
   * composer footer. Set on the full page so the footer's opaque background
   * stops short of the scrollbar, letting it bleed to the bottom edge.
   * Defaults to 0 (footer spans the full width, e.g. in the popover).
   */
  footerScrollbarGutterPx?: number;
  /**
   * Horizontal padding (theme spacing units) inside the composer footer.
   * Full-page centers the input via max width so the default 16px side gap is
   * plenty; the popover raises it so the input keeps its 24px content margin
   * now that the surrounding wrapper no longer insets the scroll surface.
   */
  footerContentPaddingX?: number;
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const messageAreaRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLElement>(null);
  const didApplyInitialScrollRef = useRef(false);
  const lastScrolledTurnAnchorKeyRef = useRef<string | null>(null);
  const previousConversationIdRef = useRef(conversationId);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const [footerHeightPx, setFooterHeightPx] = useState(0);
  const awaitingLargeDocumentOptIns = largeDocumentOptIns ?? [];
  const showLargeDocumentOptInButtons =
    hasScoutProAccess &&
    awaitingLargeDocumentOptIns.length > 0 &&
    isCurrentAgentTurnComplete({ activeTurn, streamStatus, messages });
  const measuredFooterClearancePx = footerHeightPx + SCOUT_FOOTER_CLEARANCE_GAP_PX;
  const footerClearancePx = showFooter
    ? Math.max(SCOUT_DEFAULT_FOOTER_CLEARANCE_PX, measuredFooterClearancePx)
    : 0;
  const bottomFadeClearancePx = showFooter
    ? Math.max(bottomFadeHeight ?? 0, footerClearancePx + SCOUT_BOTTOM_FADE_EXTRA_PX)
    : bottomFadeHeight;
  const jumpToLatestBottomPx = showFooter
    ? Math.max(
        SCOUT_JUMP_TO_LATEST_DEFAULT_BOTTOM_PX,
        footerHeightPx + SCOUT_JUMP_TO_LATEST_FOOTER_GAP_PX
      )
    : 16;

  useEffect(() => {
    const previousConversationId = previousConversationIdRef.current;
    previousConversationIdRef.current = conversationId;

    // Optimistic → persisted id reconcile is the same in-flight thread. Resetting
    // scroll state here re-triggers scrollToLatest when the turn finishes and
    // yanks the viewport to the bottom.
    if (
      isScoutOptimisticConversationId(previousConversationId) &&
      conversationId !== undefined &&
      !isScoutOptimisticConversationId(conversationId)
    ) {
      return;
    }

    if (previousConversationId === conversationId) {
      return;
    }

    didApplyInitialScrollRef.current = false;
    lastScrolledTurnAnchorKeyRef.current = null;
  }, [conversationId]);

  const updateJumpToLatestState = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      setShowJumpToLatest(false);
      return;
    }

    const distanceFromBottom =
      scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight;
    setShowJumpToLatest(distanceFromBottom > JUMP_TO_LATEST_THRESHOLD_PX);
  }, []);

  const scrollToLatest = useCallback((behavior: ScrollBehavior = "auto") => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }

    scrollContainer.scrollTo({
      top: scrollContainer.scrollHeight,
      behavior,
    });
  }, []);

  const handleJumpToLatest = useCallback(() => {
    scrollToLatest("smooth");
    setShowJumpToLatest(false);
  }, [scrollToLatest]);

  useEffect(() => {
    const messageArea = messageAreaRef.current;
    if (!messageArea || typeof ResizeObserver === "undefined") {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      updateJumpToLatestState();
    });
    resizeObserver.observe(messageArea);
    return () => resizeObserver.disconnect();
  }, [updateJumpToLatestState]);

  const updateFooterHeight = useCallback(() => {
    const footer = footerRef.current;
    if (!showFooter || !footer) {
      setFooterHeightPx(0);
      return;
    }

    const nextHeight = Math.ceil(footer.getBoundingClientRect().height);
    setFooterHeightPx((previousHeight) =>
      previousHeight === nextHeight ? previousHeight : nextHeight
    );
    updateJumpToLatestState();
  }, [showFooter, updateJumpToLatestState]);

  useLayoutEffect(() => {
    updateFooterHeight();

    const footer = footerRef.current;
    if (!showFooter || !footer || typeof ResizeObserver === "undefined") {
      return;
    }

    const resizeObserver = new ResizeObserver(updateFooterHeight);
    resizeObserver.observe(footer);
    return () => resizeObserver.disconnect();
  }, [showFooter, updateFooterHeight]);

  const lastRetryPayload = getLastUserTurnForRetry(messages);
  const lastUserMessage = lastRetryPayload?.text || undefined;

  const handleRetry = useCallback(() => {
    if (!lastRetryPayload || !canRetryLastUserTurn(lastRetryPayload)) {
      return;
    }

    void onSubmit(lastRetryPayload.text, {
      attachments:
        lastRetryPayload.attachments.length > 0 ? lastRetryPayload.attachments : undefined,
      attachmentPreviews:
        lastRetryPayload.attachmentPreviews.length > 0
          ? lastRetryPayload.attachmentPreviews
          : undefined,
      extractionWatchPrivateDocumentIds:
        lastRetryPayload.extractionWatchPrivateDocumentIds.length > 0
          ? lastRetryPayload.extractionWatchPrivateDocumentIds
          : undefined,
    });
  }, [lastRetryPayload, onSubmit]);

  return (
    <ScoutChatDropzone
      sx={{
        flex: 1,
        minHeight: 0,
        position: "relative",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        ref={messageAreaRef}
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <MessageList
          messages={messages}
          activeTurn={activeTurn}
          streamStatus={streamStatus}
          onRetry={handleRetry}
          canRetry={canRetryLastUserTurn(lastRetryPayload)}
          loading={loading}
          isTurnRunning={isSending}
          initialScrollToLatest={initialPinned}
          didApplyInitialScrollRef={didApplyInitialScrollRef}
          lastScrolledTurnAnchorKeyRef={lastScrolledTurnAnchorKeyRef}
          showFooter={showFooter}
          scrollContainerRef={scrollContainerRef}
          onScrollStateChange={updateJumpToLatestState}
          scrollToLatest={scrollToLatest}
          onLoadOlderMessages={onLoadOlderMessages}
          isLoadingOlderMessages={isLoadingOlderMessages}
          hasOlderMessages={hasOlderMessages}
          tenantId={tenantId}
          conversationId={conversationId}
          privateDocuments={privateDocuments}
          footerClearancePx={footerClearancePx}
          messagePaddingTop={messagePaddingTop}
          headerOverlayPx={headerOverlayPx}
        />
        <BottomCanvasFade height={bottomFadeClearancePx} />
        <Stack
          data-testid="scout-jump-to-latest"
          direction="row"
          justifyContent="center"
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: `${jumpToLatestBottomPx}px`,
            zIndex: 3,
            opacity: showJumpToLatest ? 1 : 0,
            pointerEvents: showJumpToLatest ? "auto" : "none",
            transition: "opacity 120ms ease",
          }}
        >
          <Tooltip title="Jump to latest">
            <IconButton
              aria-label="Jump to latest Scout message"
              color="primary"
              onClick={handleJumpToLatest}
              size="small"
              sx={{
                bgcolor: "background.paper",
                border: 1,
                borderColor: "divider",
                boxShadow: 1,
                "&:hover": {
                  bgcolor: "background.paper",
                },
              }}
            >
              <KeyboardArrowDown />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      {showFooter && (
        <ChatFooter
          tenantId={tenantId}
          conversationId={conversationId}
          largeDocumentOptIns={awaitingLargeDocumentOptIns}
          showLargeDocumentOptInButtons={showLargeDocumentOptInButtons}
          onDismissLargeDocumentOptIn={onDismissLargeDocumentOptIn}
          onConfirmLargeDocumentAnalysis={onConfirmLargeDocumentAnalysis}
          isConfirmingLargeDocumentAnalysis={isConfirmingLargeDocumentAnalysis}
          isDecliningLargeDocumentAnalysis={isDecliningLargeDocumentAnalysis}
          onSubmit={onSubmit}
          onAbort={onAbort}
          isSending={isSending}
          isAborting={isAborting}
          autoFocus={inputAutoFocus}
          onAttachmentsUploaded={onAttachmentsUploaded}
          onAttachmentRemoved={onAttachmentRemoved}
          writeMode={writeMode}
          onWriteModeChange={onWriteModeChange}
          hasScoutProAccess={hasScoutProAccess}
          lastUserMessage={lastUserMessage}
          inputPlaceholder={inputPlaceholder}
          footerRef={footerRef}
          scrollbarGutterPx={footerScrollbarGutterPx}
          contentPaddingX={footerContentPaddingX}
        />
      )}
    </ScoutChatDropzone>
  );
}

function BottomCanvasFade({ height }: { height?: number }) {
  const theme = useTheme();
  const canvasColor = theme.palette.scoutCanvas;

  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        left: 0,
        right: "16px",
        bottom: 0,
        zIndex: 1,
        height: height ?? { xs: 240, sm: 224 },
        pointerEvents: "none",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          maxWidth: "1000px",
          width: "100%",
          height: "100%",
          background: `linear-gradient(to bottom, ${alpha(
            canvasColor,
            0
          )} 0%, ${alpha(canvasColor, 0.9)} 48%, ${canvasColor} 72%)`,
        }}
      />
    </Box>
  );
}

function MessageList({
  messages,
  activeTurn,
  streamStatus,
  onRetry,
  canRetry,
  loading,
  isTurnRunning,
  initialScrollToLatest,
  didApplyInitialScrollRef,
  lastScrolledTurnAnchorKeyRef,
  showFooter,
  scrollContainerRef,
  onScrollStateChange,
  scrollToLatest,
  onLoadOlderMessages,
  isLoadingOlderMessages,
  hasOlderMessages,
  tenantId,
  conversationId,
  privateDocuments,
  footerClearancePx,
  messagePaddingTop,
  headerOverlayPx = 0,
}: {
  tenantId?: string;
  conversationId?: string;
  messages: ScoutChatMessage[];
  activeTurn?: ScoutActiveTurn;
  streamStatus?: StreamStatus;
  onRetry: () => void;
  canRetry: boolean;
  loading: boolean;
  isTurnRunning: boolean;
  initialScrollToLatest: boolean;
  didApplyInitialScrollRef: RefObject<boolean>;
  lastScrolledTurnAnchorKeyRef: RefObject<string | null>;
  showFooter: boolean;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  onScrollStateChange: () => void;
  scrollToLatest: (behavior?: ScrollBehavior) => void;
  onLoadOlderMessages?: () => Promise<void>;
  isLoadingOlderMessages: boolean;
  hasOlderMessages: boolean;
  privateDocuments?: ScoutConversationPrivateDocumentRow[];
  footerClearancePx: number;
  messagePaddingTop?: number;
  headerOverlayPx?: number;
}) {
  const theme = useTheme();
  const canvasColor = theme.palette.scoutCanvas;
  const scrollbarThumbColor = theme.palette.scrollbarThumb;
  const hasTriggeredTopFetchRef = useRef(false);
  const prependAnchorRef = useRef<{ scrollTop: number; scrollHeight: number } | null>(null);
  const activeTurnRef = useRef<HTMLDivElement>(null);
  const clampActiveTurnScrollRef = useRef<() => void>(() => {});
  const TOP_FETCH_THRESHOLD_PX = 80;
  const [viewportHeight, setViewportHeight] = useState(0);

  const handleScroll = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }

    clampActiveTurnScrollRef.current();

    const isNearTop = scrollContainer.scrollTop <= TOP_FETCH_THRESHOLD_PX;
    if (!isNearTop) {
      hasTriggeredTopFetchRef.current = false;
    } else if (
      hasOlderMessages &&
      !isLoadingOlderMessages &&
      onLoadOlderMessages &&
      !hasTriggeredTopFetchRef.current
    ) {
      hasTriggeredTopFetchRef.current = true;
      prependAnchorRef.current = {
        scrollTop: scrollContainer.scrollTop,
        scrollHeight: scrollContainer.scrollHeight,
      };
      void onLoadOlderMessages();
    }

    onScrollStateChange();
  }, [
    hasOlderMessages,
    isLoadingOlderMessages,
    onLoadOlderMessages,
    onScrollStateChange,
    scrollContainerRef,
  ]);

  useEffect(() => {
    onScrollStateChange();
  }, [messages, onScrollStateChange]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || typeof ResizeObserver === "undefined") {
      return;
    }

    const updateViewportHeight = () => {
      setViewportHeight(scrollContainer.clientHeight);
    };
    updateViewportHeight();

    const resizeObserver = new ResizeObserver(updateViewportHeight);
    resizeObserver.observe(scrollContainer);
    return () => resizeObserver.disconnect();
  }, [scrollContainerRef]);

  useLayoutEffect(() => {
    if (isLoadingOlderMessages) {
      return;
    }

    const anchor = prependAnchorRef.current;
    if (!anchor) {
      return;
    }

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      prependAnchorRef.current = null;
      return;
    }

    const deltaHeight = scrollContainer.scrollHeight - anchor.scrollHeight;
    if (deltaHeight > 0) {
      scrollContainer.scrollTop = anchor.scrollTop + deltaHeight;
    }
    prependAnchorRef.current = null;
  }, [isLoadingOlderMessages, messages, scrollContainerRef]);

  // True only for mid-turn failures where Scout started a turn and then
  // couldn't finish. Failures that prevented the turn from ever starting
  // (cost limit, auth, etc.) carry `couldNotStart` and render their message
  // inline without the retry bubble — retrying would just hit the same wall.
  const isMidTurnFailure =
    activeTurn?.status === "error" && !!activeTurn.error && !activeTurn.couldNotStart;

  // Drop stale empty assistant placeholders — these are blank bubbles left
  // behind by a previous failed turn. Without this, clicking Retry on the
  // apology leaves the old empty placeholder in the array; once the new
  // turn starts, isMidTurnFailure flips false, the apology unrenders, and
  // the user sees the leftover blank bubble alongside the new streaming
  // placeholder.
  //
  // "Stale empty" = assistant message that's no longer streaming AND has
  // nothing the customer UI will render. Reasoning parts don't count (we
  // intentionally hide reasoning content from customers) and step_start
  // parts on their own are just placeholders — a message containing only
  // those reads as blank.
  const isStaleEmptyAssistant = (message: ScoutChatMessage): boolean => {
    if (message.role !== "assistant") return false;
    if (message.isStreaming) return false;
    const hasVisibleContent = message.parts.some((part) => {
      if (part.type === "reasoning" || part.type === "step") return false;
      if (part.type === "text") return !!part.text?.trim();
      return true;
    });
    return !hasVisibleContent;
  };

  // Hide synthetic user messages that the UI auto-sent after the user acted
  // on an inline confirmation card. Scout still receives the underlying
  // message; the user just shouldn't see their own click rendered as a chat
  // bubble. See scoutInternalEvents.ts for why this is a text
  // sentinel and not a typed message-part field.
  let visibleMessages = messages.filter(
    (message) => !isStaleEmptyAssistant(message) && !isSyntheticInternalEventMessage(message)
  );

  // On a mid-turn failure, also drop the trailing assistant message even if
  // it has partial content — the apology + retry stands in for it. Position-
  // based rather than id-based because the optimistic-vs-real requestId
  // reconciliation can leave the placeholder keyed unpredictably.
  if (
    isMidTurnFailure &&
    visibleMessages.length > 0 &&
    visibleMessages[visibleMessages.length - 1].role === "assistant"
  ) {
    visibleMessages = visibleMessages.slice(0, -1);
  }

  const conversationTodoEpochs = useMemo(
    () => deriveConversationTodoEpochs(visibleMessages),
    [visibleMessages]
  );

  const hideConversationTodoToolRows = conversationTodoEpochs.length > 0;

  const lastUserMessage = findLastUserMessage(visibleMessages);
  const turnAnchorKey = lastUserMessage ? getMessageInstanceKey(lastUserMessage) : undefined;

  const lastUserMessageIndex = useMemo(() => {
    if (!lastUserMessage) {
      return -1;
    }
    return visibleMessages.findIndex(
      (message) => getMessageInstanceKey(message) === getMessageInstanceKey(lastUserMessage)
    );
  }, [lastUserMessage, visibleMessages]);

  const hasStreamingAssistantMessage = visibleMessages.some(
    (message) => message.role === "assistant" && Boolean(message.isStreaming)
  );

  const isActiveTurnInProgress =
    activeTurn?.status === "starting" ||
    activeTurn?.status === "streaming" ||
    hasStreamingAssistantMessage;

  // Keep the latest turn tall enough to hold the user bubble under the title
  // even after streaming finishes. Collapsing this when the turn completes
  // shrinks scrollHeight and the browser clamps scrollTop downward — a jump
  // to the bottom whenever there's enough history above the turn.
  const activeTurnMinHeight =
    lastUserMessageIndex >= 0 && viewportHeight > 0
      ? Math.max(0, viewportHeight - SCOUT_TURN_ANCHOR_TOP_GAP_PX - footerClearancePx)
      : undefined;

  const getTurnAnchorElement = useCallback((): HTMLElement | null => {
    const turnAnchor = activeTurnRef.current?.querySelector('[data-scout-turn-anchor="true"]');
    return turnAnchor instanceof HTMLElement ? turnAnchor : null;
  }, []);

  const clampScrollToKeepTurnAnchorVisible = useCallback(() => {
    if (!isActiveTurnInProgress && activeTurnMinHeight === undefined) {
      return;
    }

    const scrollContainer = scrollContainerRef.current;
    const activeTurnContainer = activeTurnRef.current;
    const turnAnchor = getTurnAnchorElement();
    if (!scrollContainer || !activeTurnContainer || !turnAnchor) {
      return;
    }

    // The active turn reserves room below the latest exchange so the user
    // bubble can sit near the top. Let users scroll far enough to read the
    // reply, but not past the reply into that reserved spacer. Offset by the
    // header overlay so the anchor rests just below it, not hidden behind it.
    const anchorOffset = Math.max(
      0,
      getTurnAnchorContentOffset(scrollContainer, turnAnchor) - headerOverlayPx
    );
    const contentBottomOffset = getActiveTurnContentBottomOffset(
      scrollContainer,
      activeTurnContainer
    );
    const composerClearancePx = showFooter ? footerClearancePx : 0;
    const maxScrollTop = Math.max(
      anchorOffset,
      contentBottomOffset - scrollContainer.clientHeight + composerClearancePx
    );
    if (scrollContainer.scrollTop > maxScrollTop) {
      scrollContainer.scrollTop = maxScrollTop;
    }
  }, [
    activeTurnMinHeight,
    footerClearancePx,
    getTurnAnchorElement,
    headerOverlayPx,
    isActiveTurnInProgress,
    scrollContainerRef,
    showFooter,
  ]);

  clampActiveTurnScrollRef.current = clampScrollToKeepTurnAnchorVisible;

  const scrollTurnAnchorIntoView = useCallback(
    (behavior: ScrollBehavior = "smooth") => {
      requestAnimationFrame(() => {
        const turnAnchor = getTurnAnchorElement();
        const scrollContainer = scrollContainerRef.current;
        if (!turnAnchor || !scrollContainer) {
          onScrollStateChange();
          return;
        }

        scrollContainer.scrollTo({
          top: Math.max(
            0,
            getTurnAnchorContentOffset(scrollContainer, turnAnchor) - headerOverlayPx
          ),
          behavior,
        });
        onScrollStateChange();
      });
    },
    [getTurnAnchorElement, headerOverlayPx, onScrollStateChange, scrollContainerRef]
  );

  useLayoutEffect(() => {
    if (loading) {
      return;
    }

    const shouldScrollToTurnAnchor =
      isTurnRunning ||
      activeTurn?.status === "starting" ||
      activeTurn?.status === "streaming" ||
      hasStreamingAssistantMessage;

    // First render of a conversation: one-shot scroll so the latest turn is in
    // view. Scroll to the turn anchor (the last user bubble) rather than the
    // very bottom — the last turn reserves a viewport-tall spacer, so
    // scrollToLatest would park in that empty canvas and push the exchange
    // above the fold. A live send animates smoothly; a cold open jumps
    // instantly. Fall back to scrollToLatest only when there's no user turn to
    // anchor on (e.g. an assistant-only thread).
    if (initialScrollToLatest && !didApplyInitialScrollRef.current && messages.length > 0) {
      didApplyInitialScrollRef.current = true;
      lastScrolledTurnAnchorKeyRef.current = turnAnchorKey ?? null;
      if (turnAnchorKey) {
        scrollTurnAnchorIntoView(shouldScrollToTurnAnchor ? "smooth" : "auto");
        return;
      }
      requestAnimationFrame(() => {
        scrollToLatest();
        onScrollStateChange();
      });
      return;
    }

    if (!turnAnchorKey || turnAnchorKey === lastScrolledTurnAnchorKeyRef.current) {
      return;
    }

    lastScrolledTurnAnchorKeyRef.current = turnAnchorKey;

    if (!shouldScrollToTurnAnchor) {
      return;
    }

    scrollTurnAnchorIntoView();
  }, [
    activeTurn?.status,
    didApplyInitialScrollRef,
    hasStreamingAssistantMessage,
    initialScrollToLatest,
    isTurnRunning,
    lastScrolledTurnAnchorKeyRef,
    loading,
    messages.length,
    onScrollStateChange,
    scrollToLatest,
    scrollTurnAnchorIntoView,
    turnAnchorKey,
  ]);

  const messagesBeforeActiveTurn =
    lastUserMessageIndex >= 0 ? visibleMessages.slice(0, lastUserMessageIndex) : visibleMessages;
  const activeTurnMessages =
    lastUserMessageIndex >= 0 ? visibleMessages.slice(lastUserMessageIndex) : [];

  const renderMessage = (message: ScoutChatMessage) => {
    if (message.role === "user") {
      return (
        <UserMessage
          key={message.clientInstanceId ?? message.id}
          message={message}
          tenantId={tenantId}
          conversationId={conversationId}
          privateDocuments={privateDocuments}
          isTurnAnchor={
            turnAnchorKey !== undefined && getMessageInstanceKey(message) === turnAnchorKey
          }
        />
      );
    }

    return (
      <AgentMessage
        key={message.id}
        message={message}
        activeTurn={activeTurn}
        streamStatus={streamStatus}
        hostedTodoEpochs={getHostedTodoEpochsForMessage(conversationTodoEpochs, message.id)}
        hideConversationTodoToolRows={hideConversationTodoToolRows}
      />
    );
  };

  return (
    <Box
      component="main"
      ref={scrollContainerRef}
      onScroll={handleScroll}
      tabIndex={-1}
      sx={{
        flex: 1,
        minHeight: 0,
        width: "100%",
        backgroundColor: canvasColor,
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarColor: `${scrollbarThumbColor} ${canvasColor}`,
        "&::-webkit-scrollbar": {
          backgroundColor: canvasColor,
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: canvasColor,
          border: "none",
          boxShadow: "none",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: scrollbarThumbColor,
          border: "none",
          borderRadius: 999,
        },
        "&::-webkit-scrollbar-corner": {
          backgroundColor: canvasColor,
        },
      }}
    >
      <Box
        sx={{
          maxWidth: "1000px",
          width: "100%",
          mx: "auto",
          // Clear the floating header overlay (full page) plus a breathing gap
          // so the first message doesn't hide behind it. The scroll surface
          // itself still extends under the header so the scrollbar bleeds to
          // the top.
          pt: messagePaddingTop ?? {
            xs: `calc(${theme.spacing(8)} + ${headerOverlayPx}px)`,
            sm: `calc(${theme.spacing(9)} + ${headerOverlayPx}px)`,
          },
          // Reserve space for the measured overlay footer/composer so the
          // last message isn't hidden behind uploaded-file previews.
          pb: showFooter ? `${footerClearancePx}px` : { xs: 4, sm: 4 },
        }}
      >
        {isLoadingOlderMessages ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", textAlign: "center", py: 1 }}
          >
            Loading older messages...
          </Typography>
        ) : null}
        {loading ? <ConversationSkeleton /> : null}
        {!loading ? messagesBeforeActiveTurn.map((message) => renderMessage(message)) : null}
        {!loading && activeTurnMessages.length > 0 ? (
          <Box
            ref={activeTurnRef}
            data-testid="scout-active-turn"
            sx={{
              minHeight: activeTurnMinHeight,
            }}
          >
            {activeTurnMessages.map((message) => renderMessage(message))}
            {isMidTurnFailure ? (
              <TurnErrorMessage
                onRetry={onRetry}
                canRetry={canRetry}
                terminalErrorCode={activeTurn?.error}
              />
            ) : null}
          </Box>
        ) : null}
        {!loading && activeTurnMessages.length === 0 && isMidTurnFailure ? (
          <TurnErrorMessage
            onRetry={onRetry}
            canRetry={canRetry}
            terminalErrorCode={activeTurn?.error}
          />
        ) : null}
      </Box>
    </Box>
  );
}

// Mirrors the outer shell of AgentMessage so the failed turn reads as a
// normal Scout message that happens to contain an apology and a retry,
// not as a separate banner appended after a blank message.
function TurnErrorMessage({
  onRetry,
  canRetry,
  terminalErrorCode,
}: {
  onRetry: () => void;
  canRetry: boolean;
  // The canonical terminal-error code from the active turn (the SSE
  // `error` event payload, e.g. "scoutCostLimitExceeded"). Drives both
  // the customer-visible copy and whether the retry affordance renders
  // at all (some codes — like cost-limit breaches — won't recover from
  // a retry, so the button gets hidden regardless of `canRetry`).
  terminalErrorCode?: string;
}) {
  const effectiveCanRetry = canRetry && !isNonRetriableTerminalErrorCode(terminalErrorCode);
  const { rowPaddingX, assistantContentPaddingX, assistantContentMaxWidth } =
    useScoutMessageSpacing();
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        py: 1.75,
        px: rowPaddingX,
      }}
    >
      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={1}
        sx={{
          maxWidth: assistantContentMaxWidth,
          px: assistantContentPaddingX,
          py: 1.5,
          borderRadius: 3,
          bgcolor: "transparent",
          color: "text.primary",
          overflowWrap: "break-word",
        }}
      >
        <ScoutIcon sx={{ color: "text.secondary", mt: 0.125 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" color="text.primary">
            {getTurnErrorMessage({ canRetry: effectiveCanRetry, code: terminalErrorCode })}
          </Typography>
          {effectiveCanRetry ? (
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
              <Button onClick={onRetry} variant="outlined" size="small">
                Retry
              </Button>
            </Stack>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
}

function ConversationSkeleton() {
  const { rowPaddingX } = useScoutMessageSpacing();
  return (
    <Stack spacing={3} sx={{ px: rowPaddingX, py: 1.5 }}>
      <Stack alignItems="flex-end">
        <Skeleton
          variant="rounded"
          width="54%"
          height={72}
          sx={{ borderRadius: 3, bgcolor: "action.hover" }}
        />
      </Stack>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Skeleton variant="circular" width={24} height={24} />
        <Stack spacing={1} sx={{ flex: 1, maxWidth: "72%" }}>
          <Skeleton variant="text" width="86%" />
          <Skeleton variant="text" width="74%" />
          <Skeleton variant="text" width="52%" />
        </Stack>
      </Stack>
    </Stack>
  );
}
